<template lang="pug">
  v-layout( row wrap )
    v-flex.xs12
      Navbar

    //- Loader
    responsive-flex(v-if="loading")
      fan-loader(
        :loading="loading" 
        :class="$vuetify.breakpoint.smAndUp ? 'my-4' : null"
      )
        div(slot="loadingMsg"
            class="full-width text-xs-center headline my-2"
        ) Submitting application...
    responsive-flex(v-else)
      v-layout(wrap)
        v-flex.text-xs-left.xs12.pt-5.px-3
          div.title.mb-2
            | Loan Amount
          div.display-2.mb-4
            | {{ loanAmount }}
          div.mb-1
            img(v-if='lender.logoSvg' :src='lender.logoSvg' height='40')
          div.headline.mb-3(v-if='loanPlan.planDescription')
            | {{ loanPlan.planDescription }}
      v-flex.xs12.px-5(v-if="lender.id === 'fortiva'")
        div( class="caption text-xs-left mt-2")
          | {{ ageDisclaimer }}

      v-flex.xs12
        v-list.pa-4
          template(
            v-for="field in customerDataFields"
          )
            div(
              :key="field.value"
              v-if="field.value !== ''"
            )
              LabelValueTile(
                :label="field.label"
                :value="field.value"
              )
              v-divider
          template(
            v-for="field in spouseDataFields"
          )
            div(
              :key="field.value"
              v-if="field.value !== '' && isMarried"
            )
              LabelValueTile(
                :label="field.label"
                :value="field.value"
              )
              v-divider
          template(
            v-if="hasCoapplicant"
          )
            v-list-tile
              div(
                class="text-xs-center headline mt-5 full-width"
              )
                //- Co-Applicant
            template(
              v-for="field in coapplicantDataFields"
            )
              div(
                v-if="field.value !== ''"
                :key="field.value"
              )
                LabelValueTile(
                  :label="field.label"
                  :value="field.value"
                )
                v-divider
      v-flex.xs12(
        class="px-3 pt-2 text-xs-left caption"
      )
        div(
          v-if="lender.id === 'fortiva'"
          class="full-width"
          v-html="grossAnnualIncome.fortivaDisclaimerText"
        )
      v-alert.mt-3.mx-3(
        v-show="greenskyPreQual"
        outline
        :color="'success'"
        :value="true"
      ) {{ alertText }}
      AppNextButton(
        v-if="greenskyPreQual"
        :buttonFunction="finishPreQualApplication"
      )
        v-icon(class="mr-2 subheading") lock
        |  Submit PreQualification
      AppNextButton(
        v-else
        class="mt-3 next-btn"
        :buttonFunction="submitApplication"
      ) Next
      v-flex.xs12(
        class="px-3 pt-2 text-xs-left caption"
      )
        div(
          v-if="greenskyWithNoPreQual || greenskyPreQual"
          class="full-width"
          v-html="greenskyMainFooter"
        )

</template>

<script>
  import AppNextButton from 'Components/AppNextButton.vue'
  import Navbar from 'Components/Navbar.vue'
  import FanLoader from 'Components/FanLoader'
  import LenderCard from 'Components/LenderCard.vue'
  import LabelValueTile from 'Components/LabelValueTile.vue'
  import { ageDisclaimer, grossAnnualIncomeData, greenskyFooter } from 'Helpers/CreditApplicationFormHelper.js'
  import { mapActions, mapState } from 'vuex'
  import ResponsiveFlex from 'Components/ResponsiveFlex.vue'

  export default {
    name: 'ReviewApplication',
    components: {
      AppNextButton,
      Navbar,
      FanLoader,
      LabelValueTile,
      LenderCard,
      ResponsiveFlex,
    },
    data () {
      return {
        ageDisclaimer,
        grossAnnualIncome: {},
        grossAnnualIncomeData,
        customerDataFields: [],
        coapplicantDataFields: [],
        spouseDataFields: [],
        greenskyMainFooter: '',
        loading: false,
      }
    },
    computed: {
      ...mapState({
        customerData: state => state.loanApplication.customerData,
        coapplicantData: state => state.loanApplication.coapplicantData,
        hasCoapplicant: state => state.loanApplication.hasCoapplicant,
        hasPreQual: state => state.preQualification.hasPreQual,
        lender: state => state.loanApplication.lender,
        pace: state => state.loanApplication.paceEligibility,
        loanPlan: state => state.loanApplication.loanPlan,
        loanAmount: state => state.loanApplication.loanAmount,
      }),
      alertText() {
        return 'PREQUALIFY WITH NO IMPACT TO YOUR CREDIT SCORE' 
      },
      greenskyPreQual() {
        return this.lender.id === 'greensky' && this.hasPreQual
      },
      greenskyWithNoPreQual() {
        return this.lender.id === 'greensky' && !this.hasPreQual
      },
      backPath () {
        if (this.hasCoapplicant) {
          return '/credit-application/coapplicant-page-two'
        } else {
          return '/credit-application/form-page-two'
        }
      },
      isMarried () {
        return this.customerData.maritalStatus === 'Married'
      },
    },
    methods: {
      ...mapActions([
        'submitPreQualification',
        'getGreenskyLegalContent',
        'error',
      ]),
      async finishPreQualApplication() {
        this.loading = true
        try {
          await this.submitPreQualification()
          this.$router.push({
            path: '/prequal-results'
          })
        } catch(error) {
          let msg
          if (error.response.data.name === 'ValidationError') {
            msg = `We could not validate your information, please try again. If this issue persists, please contact us at the number below or through the Intercom button and provide us the Error name and a screenshot if possible.`
          } 
          else if (error.response.data.name === 'ProviderError') {
            msg = `Due to a technical issue we were unable to reach ${this.lender.nickName}. For your security, your information was not saved. Please try again at a later time. If this issue persists, please contact our Customer Success Team.`
          }
          else if (error.response.data.name === 'DuplicateApplicationError') {
            const formattedFirstName = this.customerData.firstName.charAt(0).toUpperCase() + this.customerData.firstName.slice(1).toLowerCase()
            const formattedLastName = this.customerData.lastName.charAt(0).toUpperCase() + this.customerData.lastName.slice(1).toLowerCase()
            msg = `We've found an existing application for ${formattedFirstName} ${formattedLastName} and cannot process this application as a result. If this was unexpected, please contact the number below.`
          }
          else {
            msg = "We're experiencing a technical issue, we apologize for the inconvenience. If this issue persists, please contact our Customer Success Team."
          }
          this.error(`${msg}`, `${error.response.data.message}: ${error.response.data.name}`)
        }
      },
      submitApplication () {
        this.$router.push({
          path: '/credit-application/submit-application'
        })
      }
    },
    mounted: async function () {
      if (!this.customerData.firstName) {
        this.$router.push({
          path: '/credit-application/main-form'
        })
      }

      const {dateOfBirth} = this.customerData
      this.customerDataFields = [
        {
          label: 'First Name',
          value: this.customerData.firstName
        },
        {
          label: 'Last Name',
          value: this.customerData.lastName
        },
        {
          label: 'Address',
          value: (this.customerData.streetAddress + ' ' +
            this.customerData.aptNumber).trim()
        },
        {
          label: 'City, State, Zip',
          value: this.customerData.city + ', ' +
          this.customerData.state + ' ' +
          this.customerData.zipCode
        },
        {
          label: 'Date of Birth',
          value: [
            dateOfBirth.slice(0, 2),
            dateOfBirth.slice(2, 4),
            dateOfBirth.slice(4)
          ].join('/')
        },
        {
          label: 'Social Security Number',
          value: 'XXX-XX-' + this.customerData.ssNumber.slice(5)
        },
        {
          label: 'Phone Number',
          value: '(' + this.customerData.phoneNumber.slice(0, 3) +
          ') ' + this.customerData.phoneNumber.slice(3, 6) +
          '-' + this.customerData.phoneNumber.slice(6)
        },
        {
          label: 'Email',
          value: this.customerData.emailAddress
        },
        {
          label: 'Gross Annual Income',
          value: this.customerData.grossAnnualIncome
        },
        {
          label: 'Marital Status',
          value: this.customerData.maritalStatus
        }
      ]
      this.spouseDataFields = [
        {
          label: 'Spouse First Name',
          value: this.customerData.spouseFirstName
        },
        {
          label: 'Spouse Last Name',
          value: this.customerData.spouseLastName
        },
        {
          label: 'Spouse Address',
          value: (this.customerData.spouseStreetAddress + ' ' +
            this.customerData.spouseAptNumber).trim()
        },
        {
          label: 'Spouse City, State, Zip',
          value: this.customerData.spouseCity + ', ' +
          this.customerData.spouseState + ' ' +
          this.customerData.spouseZipCode
        },
      ]
      this.coapplicantDataFields = [
        {
          label: 'First Name',
          value: this.coapplicantData.firstName
        },
        {
          label: 'Last Name',
          value: this.coapplicantData.lastName
        },
        {
          label: 'Address',
          value: (this.coapplicantData.streetAddress + ' ' +
            this.coapplicantData.aptNumber).trim()
        },
        {
          label: 'City, State, Zip',
          value: this.coapplicantData.city + ', ' +
          this.coapplicantData.state + ' ' +
          this.coapplicantData.zipCode
        },
        {
          label: 'Date of Birth',
          value: [
            this.coapplicantData.dateOfBirth.slice(0, 2),
            this.coapplicantData.dateOfBirth.slice(2, 4),
            this.coapplicantData.dateOfBirth.slice(4)
          ].join('/')
        },
        {
          label: 'Social Security Number',
          value: 'XXX-XX-' + this.coapplicantData.ssNumber.slice(5)
        },
        {
          label: 'Phone Number',
          value: '(' + this.coapplicantData.phoneNumber.slice(0, 3) +
          ') ' + this.coapplicantData.phoneNumber.slice(3, 6) +
          '-' + this.coapplicantData.phoneNumber.slice(6)
        },
        {
          label: 'Email',
          value: this.coapplicantData.emailAddress
        },
        {
          label: 'Gross Annual Income',
          value: this.coapplicantData.grossAnnualIncome
        }
      ]
      if (this.lender.id === 'greensky'){
        this.grossAnnualIncome = await this.getGreenskyLegalContent(grossAnnualIncomeData)
        this.greenskyMainFooter = await this.getGreenskyLegalContent(greenskyFooter)
      } else {
        this.grossAnnualIncome = grossAnnualIncomeData()
        this.greenskyMainFooter = greenskyFooter()
      }
    }
  }
</script>